<template>
  <div :class="{ 'full-screen': isFullScreen }" @click="handleContainerClick">
    <div class="editor-header">
      <span class="editor-title">{{ editorTitle }}</span>
      <div class="editor-actions">
        <span class="editor-text" v-html="topSideLabelText" />
        <slot v-if="isFullScreen" name="customActions"></slot>
        <VasionButton
          class=""
          title="Settings"
          :icon="fullScreenIcon"
          :isDense="true"
          @vasionButtonClicked="toggleFullScreen"
        />
      </div>
    </div>
    <div class="textarea-container">
      <div class="line-numbers">
        <span v-for="n in lineNumbers" :key="n">{{ n }}</span>
      </div>
      <textarea
        ref="customTextBox"
        class="styled-textarea"
        data-paste-target="true"
        :value="value"
        spellcheck="false"
        @input="handleInput"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'VasionCustomizableTextBox',
  props: {
    initialLines: {
      type: Number,
      required: false,
      default: 5,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    topSideLabel: {
      type: String,
      required: false,
      default: '',
    },
    value: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      editorTitle: this.title,
      topSideLabelText: this.topSideLabel,
      lineNumbers: Array.from({ length: this.initialLines }, (_, i) => i + 1),
      isFullScreen: false,
    }
  },
  watch: {
    topSideLabel(newLabel) {
      this.topSideLabelText = newLabel
    },
    value(newValue) {
      this.updateLineNumbers(newValue)
      this.$emit('input', newValue)
    },
  },
  computed: {
    fullScreenIcon() {
      return this.isFullScreen
        ? 'VasionCloseFullscreen'
        : 'VasionOpenFullscreen'
    },
  },
  async created() {
    this.updateLineNumbers(this.value)
  },
  methods: {
    handleInput(event) {
      this.$emit('input', event.target.value)
      this.updateLineNumbers(event.target.value)
    },
    handleContainerClick(event) {
      if (event.target !== event.currentTarget) return

      const textarea = this.$refs.customTextBox
      if (textarea) {
        textarea.focus()
        const length = textarea.value.length
        textarea.setSelectionRange(length, length)
      }
    },
    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen
    },
    updateLineNumbers(value) {
      this.$nextTick(() => {const textarea = this.$refs.customTextBox;
        if (!textarea) return;

        // Create a temporary element to measure text width
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.visibility = 'hidden';
        tempDiv.style.whiteSpace = 'pre-wrap';
        tempDiv.style.wordWrap = 'break-word';
        tempDiv.style.width = `${textarea.clientWidth}px`;
        tempDiv.style.fontFamily = window.getComputedStyle(textarea).fontFamily;
        tempDiv.style.fontSize = window.getComputedStyle(textarea).fontSize;
        document.body.appendChild(tempDiv);

        // Split the value into lines and calculate wrapped lines
        const lines = value.split('\n');
        let lineNumbers = [];
        let lineNumber = 0;
        lines.forEach((line) => {
          tempDiv.textContent = line || ' ';
          const lineHeight = parseFloat(window.getComputedStyle(textarea).lineHeight);
          const wrappedLines = Math.ceil(tempDiv.offsetHeight / lineHeight);
          for (let i = 0; i < wrappedLines; i++) {
            lineNumbers.push(++lineNumber);
          }
          if (wrappedLines > 1) lineNumbers.push(++lineNumber)
        });

        // Clean up the temporary element
        document.body.removeChild(tempDiv);

        this.lineNumbers = lineNumbers;
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px;
  border-bottom: none;
  border-radius: 24px 24px 0 0;
  background: $surface-container;
}
.editor-title {
  color: $on-surface-variant;
  font-family: $font-family-book;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
}
.editor-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.editor-text {
  font-size: 14px;
  color: $on-surface-variant;
  font-family: $font-family-book;
}

.textarea-container {
  display: flex;
  font-family: monospace;
  border-radius: 0 0 4px 4px;
  overflow: hidden;
  background: $surface-container;
}

.line-numbers {
  background: $surface-container;
  padding: 8px;
  padding-top: 0;
  text-align: right;
  color: #888;
  user-select: none;
}

.line-numbers span {
  display: block;
  line-height: 1.5;
}

.styled-textarea {
  width: 100%;
  border: none;
  padding: 8px;
  padding-top: 0;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  outline: none;
  overflow: hidden;
}

.full-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #fff;
  z-index: 1000;
  overflow-y: scroll;
}
</style>
